import React from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Spin,
  Alert,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import DatePicker from '../DatePicker';
import MATCH_KIND_LABELS from './patientSearchMatchKinds';

const DATE_INPUT_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'MMDDYYYY',
  'YYYY-MM-DD',
];
const hasFieldValue = (value) => value != null && String(value).trim() !== '';
const WARNING_COPY = {
  approximate_search_unavailable: 'Some approximate recovery could not be evaluated. Only safely evaluated results are shown; retry or refine your search.',
  dob_cohort_overflow: 'The widened date-of-birth search was not evaluated because it returned too many patients. Refine the names and try again.',
};

const parseDatePickerValue = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;

  const parsed = moment(value, DATE_INPUT_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

const PatientSearchPanel = ({
  searchFields,
  searchInstructions,
  fieldValues,
  onFieldChange,
  onReset,
  results,
  isLoading,
  error,
  canSearch,
  allowResultSelection,
  onSelect,
  selectedPatient,
  searchWarnings,
  hasMore,
  isVariantSearch,
  blockedMessage,
  searchModes,
  activeMode,
  onModeChange,
  searchTrigger,
  onSearch,
}) => {
  const hasAnyValue = Object.values(fieldValues).some(hasFieldValue);
  // Highlight the schema's search instructions while the entered input is not
  // yet searchable. canSearch is schema-driven (required_groups, min length),
  // so the warning and the empty-state message tell one consistent story and
  // EMRs that allow name-only search never see a misfired warning.
  const showInstructionsWarning = !blockedMessage && hasAnyValue && !canSearch;

  const renderSearchFields = () => {
    if (!searchFields?.length) {
      // Fallback: single text input (backward compat)
      return (
        <Input
          placeholder="Search patients"
          value={fieldValues.query || ''}
          onChange={(e) => onFieldChange('query', e.target.value)}
          allowClear
          className="dynamic-ehr-drawer__patient-input"
          prefix={<SearchOutlined style={{ color: 'rgba(94, 57, 153, 0.7)' }} />}
        />
      );
    }

    // Group fields into rows based on width
    const rows = [];
    let currentRow = [];
    searchFields.forEach((field) => {
      if (field.width === 'full') {
        if (currentRow.length) rows.push(currentRow);
        rows.push([field]);
        currentRow = [];
      } else {
        currentRow.push(field);
        if (currentRow.length === 2) {
          rows.push(currentRow);
          currentRow = [];
        }
      }
    });
    if (currentRow.length) rows.push(currentRow);

    const fields = (
      <>
        {rows.map((row) => {
          const rowKey = row.map((field) => field.key).join('-');

          return (
            <div key={rowKey} className="dynamic-ehr-drawer__search-row">
              {row.map((field) => {
                const inputId = `patient-search-${field.key}`;

                return (
                  <div
                    key={field.key}
                    className={`dynamic-ehr-drawer__search-field ${field.width === 'full' ? 'full' : ''}`}
                  >
                    <label htmlFor={inputId}>
                      {field.label}
                      {field.required && <span className="required-mark"> *</span>}
                    </label>
                    {field.component === 'date' ? (
                      <DatePicker
                        id={inputId}
                        placeholder={field.placeholder || 'MM/DD/YYYY'}
                        format={DATE_INPUT_FORMATS}
                        value={parseDatePickerValue(fieldValues[field.key])}
                        onChange={(date) => {
                          onFieldChange(field.key, date ? date.format('YYYY-MM-DD') : '');
                        }}
                        allowClear
                        style={{ width: '100%' }}
                        getPopupContainer={() => document.body}
                        dropdownClassName="ehr-select-dropdown"
                      />
                    ) : (
                      <Input
                        id={inputId}
                        placeholder={field.placeholder}
                        value={fieldValues[field.key] || ''}
                        onChange={(e) => onFieldChange(field.key, e.target.value)}
                        allowClear
                      />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        {searchFields.length > 1 && hasAnyValue && (
          <button
            type="button"
            className="dynamic-ehr-drawer__search-clear-all"
            onClick={onReset}
          >
            Clear search
          </button>
        )}
      </>
    );

    if (!isVariantSearch) return fields;

    return (
      <fieldset className="dynamic-ehr-drawer__search-criteria">
        <legend>Search criteria</legend>
        <p>Changes here affect search only. Correct the patient details below if the source record is wrong.</p>
        {fields}
      </fieldset>
    );
  };

  const renderEmptyState = () => {
    if (isLoading) return null;
    if (blockedMessage) return blockedMessage;

    const hasInput = Object.values(fieldValues).some(hasFieldValue);
    if (!hasInput) return 'Type to search patients.';
    if (!canSearch) return 'Enter the required search details above to search.';

    if (isVariantSearch) {
      const firstNamePresent = hasFieldValue(fieldValues.first_name);
      const lastNamePresent = hasFieldValue(fieldValues.last_name);
      const dobPresent = hasFieldValue(fieldValues.date_of_birth);
      if (firstNamePresent !== lastNamePresent) return 'No matches found. Add the missing name or refine the name you entered.';
      if (firstNamePresent && lastNamePresent && !dobPresent) return 'No matches found. Add date of birth or refine the names.';
      if (dobPresent) return 'No safely matching chart was found. Confirm the mapped date of birth, refine the names, or verify in NextGen.';
    }

    return 'No matches yet\u2014try a different search.';
  };

  const renderWarnings = () => {
    if (!isVariantSearch) return null;

    const knownWarnings = searchWarnings.filter((code) => WARNING_COPY[code]);
    const hasSpeculativeResult = results.some((patient) => patient.speculative === true);
    const messages = [
      ...knownWarnings.map((code) => WARNING_COPY[code]),
      ...(hasMore ? ['More matches may exist. Refine the names to narrow the search.'] : []),
      ...(hasSpeculativeResult ? ['Verify this is the correct patient before selecting an approximate match.'] : []),
    ];

    if (!messages.length) return null;

    return (
      <div className="dynamic-ehr-drawer__search-warnings" role="status" aria-live="polite">
        {messages.map((message) => <div key={message}>{message}</div>)}
      </div>
    );
  };

  const renderBody = () => {
    if (error) {
      return <Alert type="error" message={error} showIcon />;
    }

    if (isLoading) {
      return (
        <div className="dynamic-ehr-drawer__patient-empty">
          <Spin size="small" />
          <span style={{ marginLeft: 8 }}>Searching…</span>
        </div>
      );
    }

    if (!results.length) {
      return (
        <div className="dynamic-ehr-drawer__patient-empty">
          {renderEmptyState()}
        </div>
      );
    }

    return (
      <ul className="dynamic-ehr-drawer__patient-result-list">
        {results.map((patient) => {
          const isSelected = selectedPatient?.value === patient.value;
          const patientName = patient.label || patient.metadata?.patient_name || 'Patient';
          const patientDob = patient.date_of_birth || patient.metadata?.date_of_birth;
          const patientMrn = patient.mrn || patient.metadata?.mrn;
          const badge = isVariantSearch ? MATCH_KIND_LABELS[patient.match_kind] : null;
          const content = (
            <>
              <div className="dynamic-ehr-drawer__patient-result-heading">
                <div className="dynamic-ehr-drawer__patient-result-label">
                  {patientName}
                </div>
                {badge && <span className={`dynamic-ehr-drawer__match-badge dynamic-ehr-drawer__match-badge--${patient.match_kind}`}>{badge}</span>}
              </div>
              <div className="dynamic-ehr-drawer__patient-result-meta">
                <span>
                  DOB:
                  {patientDob || 'unavailable'}
                </span>
                <span className="dynamic-ehr-drawer__patient-result-mrn">
                  MRN:
                  {patientMrn || 'unavailable'}
                </span>
              </div>
            </>
          );

          if (!allowResultSelection) {
            return (
              <li
                key={patient.value}
                className="dynamic-ehr-drawer__patient-result dynamic-ehr-drawer__patient-result--readonly"
              >
                {content}
              </li>
            );
          }

          return (
            <li
              key={patient.value}
              className={`dynamic-ehr-drawer__patient-result ${isSelected ? 'selected' : ''}`}
            >
              {content}
              <button
                type="button"
                className="dynamic-ehr-drawer__patient-result-select"
                aria-pressed={isSelected}
                onClick={() => onSelect(patient)}
              >
                {isSelected ? 'Selected' : `Select ${patientName}`}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderResultsCount = () => {
    if (isLoading || error || !results.length) return null;
    const count = results.length;
    return (
      <div className="dynamic-ehr-drawer__patient-count" role="status" aria-live="polite">
        {count}
        {' '}
        {count === 1 ? 'result' : 'results'}
        {' '}
        found
      </div>
    );
  };

  return (
    <div className="dynamic-ehr-drawer__patient-search">
      {searchModes.length > 1 && (
        <div className="dynamic-ehr-drawer__search-modes" role="group" aria-label="Patient search method">
          {searchModes.map((mode) => (
            <button
              type="button"
              key={mode.key}
              className={`dynamic-ehr-drawer__search-mode ${activeMode === mode.key ? 'active' : ''}`}
              aria-pressed={activeMode === mode.key}
              onClick={() => onModeChange(mode.key)}
            >
              {mode.label}
            </button>
          ))}
        </div>
      )}
      <div className="dynamic-ehr-drawer__search-fields">
        {renderSearchFields()}
      </div>
      {searchTrigger === 'explicit' && (
        <button
          type="button"
          className="dynamic-ehr-drawer__search-submit"
          onClick={onSearch}
          disabled={!canSearch || isLoading}
        >
          <SearchOutlined />
          Search
        </button>
      )}
      {searchInstructions && (
        <div
          className={[
            'dynamic-ehr-drawer__search-instructions',
            showInstructionsWarning ? 'dynamic-ehr-drawer__search-instructions--warning' : '',
          ].filter(Boolean).join(' ')}
        >
          {searchInstructions}
        </div>
      )}
      {renderWarnings()}
      {renderResultsCount()}
      <div className="dynamic-ehr-drawer__patient-results" aria-busy={isLoading}>
        {renderBody()}
      </div>
    </div>
  );
};

PatientSearchPanel.propTypes = {
  searchFields: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    component: PropTypes.oneOf(['text', 'date']).isRequired,
    placeholder: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    required: PropTypes.bool,
  })),
  searchInstructions: PropTypes.string,
  fieldValues: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  results: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string,
    mrn: PropTypes.string,
    date_of_birth: PropTypes.string,
    metadata: PropTypes.object,
    match_kind: PropTypes.oneOf(Object.keys(MATCH_KIND_LABELS)),
    speculative: PropTypes.bool,
    selection_proof: PropTypes.string,
  })).isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  canSearch: PropTypes.bool,
  allowResultSelection: PropTypes.bool,
  onSelect: PropTypes.func,
  selectedPatient: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  searchWarnings: PropTypes.arrayOf(PropTypes.string),
  hasMore: PropTypes.bool,
  isVariantSearch: PropTypes.bool,
  blockedMessage: PropTypes.string,
  searchModes: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    field_keys: PropTypes.arrayOf(PropTypes.string),
  })),
  activeMode: PropTypes.string,
  onModeChange: PropTypes.func,
  searchTrigger: PropTypes.oneOf(['automatic', 'explicit']),
  onSearch: PropTypes.func,
};

PatientSearchPanel.defaultProps = {
  searchFields: null,
  searchInstructions: null,
  isLoading: false,
  error: null,
  canSearch: true,
  allowResultSelection: true,
  onSelect: () => {},
  selectedPatient: null,
  searchWarnings: [],
  hasMore: false,
  isVariantSearch: false,
  blockedMessage: null,
  searchModes: [],
  activeMode: null,
  onModeChange: () => {},
  searchTrigger: 'automatic',
  onSearch: () => {},
};

export default PatientSearchPanel;
